'use client';

import { c as _c } from "react/compiler-runtime";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLabsStore, usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { BrandButton, Confetti } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/_components';
import { DurationTag, RecTag } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/rec-tag';
import { LogoChip, VerticalThumbnail } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/rec-thumbnail';
import { StepShell } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/step-shell';
import type { LabsRecommendation } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';
import { LABS_PREVIEW_EVENTS } from '@/lib/analytics/labs-preview-events';
import { cn } from '@/utils/cn';
import { isFieldLessPack } from '../_engine/field-less-pack';
import { usePreviewAnalytics } from '../_engine/preview-analytics';
import { artifactFileName, buildPreviewArtifact, renderArtifactHtml } from '../_engine/preview-artifact';
import { isLeadPipeConfigured, submitPreviewLead } from '../_engine/submit-preview-lead';
import { PreviewMatchModal } from './preview-match-modal';

/**
 * The public preview's last screen: the result, the externships it points to, and the ask.
 *
 * It occupies the `recommendations` step slot, where the gated sprint shows its own exit screen. The
 * two are different because the reader is different. The sprint sends a paying learner into a program
 * they can start today. This one is talking to someone who arrived from a search result minutes ago
 * and has no account, so every one of the sprint's CTAs would bounce them to /payment.
 *
 * TWO PATHS, NOT A GATE (Jay, 2026-08-26). The result is never withheld. An earlier draft put the
 * matched externships behind the email, and it optimised the wrong thing: signups are what this
 * experiment is judged on, and emails are worth much less while there is no lead endpoint and no
 * nurture list wired up. A gate taxes the reader who was ready to sign up in order to harvest an
 * address from the reader who was not.
 *
 * So the page offers both, in order of what they are worth:
 *   1. Each matched externship opens IN PLACE to show what the work actually is, and the trial CTA
 *      lives inside that panel. Three beats, not one: curious, informed, converted.
 *   2. The email ask sits below, framed as the alternative for someone who is not ready.
 *
 * The cards cannot open the catalog: every program page lives inside (subscription-gated), so a link
 * would bounce a signed-out reader straight to /payment. Expanding in place is how they get the
 * substance anyway, without a navigation that would break the flow or a wall that would end it.
 *
 * Recommendations come from the pack's static fallback, not the live catalog. `useLabsRecommendations`
 * fetches through `useQueryAuth`, which has no session here, so it would fail on every load and land
 * on this same fallback having spent a request and a loading state to get there. The fallback names
 * real programs by contract, so the cards are honest either way.
 */

const SIGN_UP_PATH = '/sign-up';

/**
 * The hook is the TRIAL, not the price (Jay, 2026-08-26). There is no public pricing page, it sits
 * behind the paywall, and leading with a number asks someone who has never heard of Extern to judge a
 * cost before they know the value. Seven days free is the offer.
 *
 * Stated HERE, once, next to the matches rather than inside the overlay. The overlay is the real
 * product component and its button says "Start now"; forking it just to reword one label would mean
 * this page stops tracking whatever the product does next. The card requirement still gets said out
 * loud before anyone taps, which is the part that matters, because finding it at checkout is how
 * goodwill gets spent.
 */
const TRIAL_TERMS = 'Free for 7 days. Card required, cancel any time.';

/**
 * Minimal email shape check, deliberately not the platform's `emailSchema`. This is a marketing
 * capture: over-rejecting a real address costs a signup, while accepting a typo costs one dead row.
 * Permissive is the right direction here.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface SignUpParams {
  email?: string;
  externshipId?: number;
  guideSource: string;
  /** The marketing choice they already made here, so sign-up does not silently re-decide it. */
  newsletter?: boolean;
  vertical: string;
}

/** Where every action on this screen ends up. UTMs carry the guide, the track and what was clicked. */
function signUpHref({
  email,
  externshipId,
  guideSource,
  newsletter,
  vertical
}: SignUpParams): string {
  const params = new URLSearchParams({
    utm_campaign: 'labs_preview',
    utm_content: vertical,
    utm_medium: 'lab',
    utm_source: guideSource
  });
  if (email) {
    params.set('email', email);
  }
  if (externshipId) {
    params.set('externshipId', String(externshipId));
  }
  // Only when they have actually been asked. Absent means sign-up keeps its own default.
  if (newsletter !== undefined) {
    params.set('newsletter', newsletter ? '1' : '0');
  }
  return `${SIGN_UP_PATH}?${params.toString()}`;
}

/**
 * One matched program. Tapping it opens the detail OVERLAY; it never navigates.
 *
 * It used to send every tap straight to sign-up, which is a bait: a reader taps the one program they
 * were curious about and lands on a bare account form that says nothing about it. Both design reviews
 * scored that as the biggest drop on the page, at the moment intent is highest.
 *
 * The in-place accordion that replaced it was worse to look at: opening a card pushed the rest of the
 * page down and the reader lost their place. The overlay leaves the payoff still underneath.
 */
function MatchCard(t0) {
  const $ = _c(34);
  const {
    accent,
    onOpen,
    rec
  } = t0;
  let t1;
  if ($[0] !== onOpen || $[1] !== rec) {
    t1 = () => onOpen(rec);
    $[0] = onOpen;
    $[1] = rec;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== accent || $[4] !== rec.company || $[5] !== rec.image || $[6] !== rec.logo) {
    t2 = <VerticalThumbnail accent={accent} company={rec.company} logo={rec.logo} src={rec.image} />;
    $[3] = accent;
    $[4] = rec.company;
    $[5] = rec.image;
    $[6] = rec.logo;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== rec.company || $[9] !== rec.logo) {
    t3 = <LogoChip className="absolute -bottom-5 left-4" company={rec.company} logo={rec.logo} />;
    $[8] = rec.company;
    $[9] = rec.logo;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="relative">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== rec.title) {
    t5 = <span className="font-instrument text-[20px] text-fs-text leading-[1.15]">{rec.title}</span>;
    $[14] = rec.title;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== rec.blurb) {
    t6 = <span className="text-fs-body-md text-fs-text-subtle leading-relaxed">{rec.blurb}</span>;
    $[16] = rec.blurb;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== rec.duration) {
    t7 = <DurationTag>{rec.duration}</DurationTag>;
    $[18] = rec.duration;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== rec.tags) {
    t8 = rec.tags.slice(0, 2).map(_temp);
    $[20] = rec.tags;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-fs-xs pt-fs-xs">{t7}{t8}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="flex items-center gap-1 pt-fs-xs font-medium text-fs-body-md text-fs-brand">See what you would actually do<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></span>;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t9) {
    t11 = <div className="flex flex-1 flex-col gap-fs-xs p-fs-md pt-8">{t5}{t6}{t9}{t10}</div>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t1 || $[31] !== t11 || $[32] !== t4) {
    t12 = <button className="group flex cursor-pointer flex-col overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface text-left shadow-[0_1px_2px_rgba(10,14,26,0.04)] transition-shadow hover:shadow-[0_8px_24px_rgba(10,14,26,0.08)]" onClick={t1} type="button">{t4}{t11}</button>;
    $[30] = t1;
    $[31] = t11;
    $[32] = t4;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp(tag) {
  return <RecTag key={tag}>{tag}</RecTag>;
}
export function PreviewPayoff() {
  const $ = _c(140);
  const pack = usePack();
  const {
    archetypes,
    axis,
    deriveArchetype,
    displayName,
    recommendations: config,
    slug
  } = pack;
  const storedArchetype = useLabsStore(_temp2);
  const m2Answers = useLabsStore(_temp3);
  const m1Answers = useLabsStore(_temp4);
  const m3Answers = useLabsStore(_temp5);
  const m3Diagnosis = useLabsStore(_temp6);
  const {
    guideSource,
    track,
    visitorId
  } = usePreviewAnalytics();
  const [openMatch, setOpenMatch] = useState(null);
  const [email, setEmail] = useState("");
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [artifactPreview, setArtifactPreview] = useState(null);
  let t0;
  let t1;
  if ($[0] !== artifactPreview) {
    t0 = () => {
      if (artifactPreview === null) {
        return;
      }
      const {
        body
      } = document;
      const scrollY = window.scrollY;
      const previousCss = body.style.cssText;
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
      body.style.overflow = "hidden";
      return () => {
        body.style.cssText = previousCss;
        window.scrollTo(0, scrollY);
      };
    };
    t1 = [artifactPreview];
    $[0] = artifactPreview;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== deriveArchetype || $[4] !== m2Answers || $[5] !== storedArchetype) {
    t2 = storedArchetype ?? deriveArchetype(m2Answers);
    $[3] = deriveArchetype;
    $[4] = m2Answers;
    $[5] = storedArchetype;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const archetypeId = t2;
  const arch = archetypes[archetypeId];
  const accent = arch?.accent ?? axis.bothAccent;
  let t3;
  if ($[7] !== archetypeId || $[8] !== config.fallback) {
    t3 = config.fallback[archetypeId] ?? [];
    $[7] = archetypeId;
    $[8] = config.fallback;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = [...t3];
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const matches = t4;
  let t5;
  if ($[12] !== m1Answers || $[13] !== m2Answers || $[14] !== m3Answers || $[15] !== m3Diagnosis || $[16] !== pack || $[17] !== storedArchetype) {
    t5 = buildPreviewArtifact(pack, {
      archetype: storedArchetype,
      m1Answers,
      m2Answers,
      m3Answers,
      m3Diagnosis
    });
    $[12] = m1Answers;
    $[13] = m2Answers;
    $[14] = m3Answers;
    $[15] = m3Diagnosis;
    $[16] = pack;
    $[17] = storedArchetype;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const artifact = t5;
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = isLeadPipeConfigured();
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const leadCaptureOn = t6;
  const t7 = artifact.archetypeName || "your result";
  let parts;
  let t8;
  if ($[20] !== artifact.diagnosis || $[21] !== artifact.fingerprint.length || $[22] !== artifact.score.answered || $[23] !== artifact.subjectName || $[24] !== t7) {
    parts = [t7];
    if (artifact.fingerprint.length > 0) {
      parts.push("your mindset fingerprint");
    }
    if (artifact.score.answered > 0) {
      const rateWord = artifact.score.answered === 1 ? "rate" : "rates";
      parts.push(`the ${artifact.score.answered} ${rateWord} you worked out on ${artifact.subjectName}`);
    }
    if (artifact.diagnosis) {
      parts.push("your own read of the case");
    }
    t8 = parts.pop();
    $[20] = artifact.diagnosis;
    $[21] = artifact.fingerprint.length;
    $[22] = artifact.score.answered;
    $[23] = artifact.subjectName;
    $[24] = t7;
    $[25] = parts;
    $[26] = t8;
  } else {
    parts = $[25];
    t8 = $[26];
  }
  const last = t8;
  const list = parts.length > 0 ? `${parts.join(", ")} and ${last}` : last ?? "";
  const artifactSummary = `A designed two-page readout: ${list}.`;
  const artifactShown = useRef(false);
  let t9;
  if ($[27] !== archetypeId || $[28] !== artifact.diagnosis || $[29] !== artifact.score.answered || $[30] !== track) {
    t9 = () => {
      if (artifactShown.current) {
        return;
      }
      artifactShown.current = true;
      track(LABS_PREVIEW_EVENTS.ARTIFACT_SHOWN, {
        archetype: archetypeId,
        has_diagnosis: artifact.diagnosis !== "",
        rates_answered: artifact.score.answered
      });
    };
    $[27] = archetypeId;
    $[28] = artifact.diagnosis;
    $[29] = artifact.score.answered;
    $[30] = track;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== archetypeId || $[33] !== artifact || $[34] !== track) {
    t10 = [archetypeId, artifact, track];
    $[32] = archetypeId;
    $[33] = artifact;
    $[34] = track;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  useEffect(t9, t10);
  let t11;
  if ($[36] !== artifact) {
    t11 = () => {
      const completedOn = new Date().toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      return renderArtifactHtml(artifact, completedOn);
    };
    $[36] = artifact;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const renderArtifactNow = t11;
  let t12;
  if ($[38] !== artifact || $[39] !== renderArtifactNow) {
    t12 = () => {
      const blob = new Blob([renderArtifactNow()], {
        type: "text/html"
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = artifactFileName(artifact);
      link.href = url;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
    $[38] = artifact;
    $[39] = renderArtifactNow;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const deliverArtifact = t12;
  let t13;
  if ($[41] !== renderArtifactNow) {
    t13 = () => {
      setArtifactPreview(renderArtifactNow());
    };
    $[41] = renderArtifactNow;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const showArtifact = t13;
  let t14;
  if ($[43] !== archetypeId || $[44] !== track) {
    t14 = opened => {
      track(LABS_PREVIEW_EVENTS.ARTIFACT_DOWNLOADED, {
        archetype: archetypeId,
        opened
      });
    };
    $[43] = archetypeId;
    $[44] = track;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const trackArtifactTaken = t14;
  let goToSignUp;
  let handleAccountFromArtifact;
  if ($[46] !== archetypeId || $[47] !== consented || $[48] !== email || $[49] !== guideSource || $[50] !== slug || $[51] !== track) {
    handleAccountFromArtifact = () => {
      track(LABS_PREVIEW_EVENTS.SIGNUP_HANDOFF, {
        archetype: archetypeId,
        path: "artifact"
      });
      goToSignUp({
        email: email.trim(),
        newsletter: consented
      });
    };
    goToSignUp = params => {
      window.location.assign(signUpHref({
        ...params,
        guideSource,
        vertical: slug
      }));
    };
    $[46] = archetypeId;
    $[47] = consented;
    $[48] = email;
    $[49] = guideSource;
    $[50] = slug;
    $[51] = track;
    $[52] = goToSignUp;
    $[53] = handleAccountFromArtifact;
  } else {
    goToSignUp = $[52];
    handleAccountFromArtifact = $[53];
  }
  let t15;
  if ($[54] !== archetypeId || $[55] !== track) {
    t15 = rec => {
      setOpenMatch(rec);
      track(LABS_PREVIEW_EVENTS.RECOMMENDATION_CLICKED, {
        archetype: archetypeId,
        company: rec.company,
        externship_id: rec.externshipId ?? null,
        externship_title: rec.title
      });
    };
    $[54] = archetypeId;
    $[55] = track;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  const handleMatchOpen = t15;
  let t16;
  if ($[57] !== archetypeId || $[58] !== goToSignUp || $[59] !== track) {
    t16 = rec_0 => {
      track(LABS_PREVIEW_EVENTS.SIGNUP_HANDOFF, {
        archetype: archetypeId,
        externship_title: rec_0.title,
        path: "externship_card"
      });
      goToSignUp({
        externshipId: rec_0.externshipId
      });
    };
    $[57] = archetypeId;
    $[58] = goToSignUp;
    $[59] = track;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  const handleStartTrial = t16;
  let t17;
  if ($[61] !== archetypeId || $[62] !== artifact.diagnosis || $[63] !== consented || $[64] !== deliverArtifact || $[65] !== email || $[66] !== guideSource || $[67] !== showArtifact || $[68] !== slug || $[69] !== track || $[70] !== trackArtifactTaken || $[71] !== visitorId) {
    t17 = event => {
      event.preventDefault();
      const trimmed = email.trim();
      if (!EMAIL_RE.test(trimmed)) {
        setError("That email does not look right. Mind checking it?");
        return;
      }
      setError(null);
      setSubmitting(true);
      track(LABS_PREVIEW_EVENTS.EMAIL_SUBMITTED, {
        archetype: archetypeId,
        email: trimmed,
        marketing_consent: consented
      });
      submitPreviewLead({
        archetype: archetypeId,
        email: trimmed,
        guideSource,
        marketingConsent: consented,
        vertical: slug,
        visitorId
      });
      track(LABS_PREVIEW_EVENTS.ARTIFACT_UNLOCKED, {
        archetype: archetypeId,
        lead_capture: leadCaptureOn,
        has_diagnosis: artifact.diagnosis !== ""
      });
      setUnlocked(true);
      setSubmitting(false);
      deliverArtifact();
      showArtifact();
      trackArtifactTaken(true);
    };
    $[61] = archetypeId;
    $[62] = artifact.diagnosis;
    $[63] = consented;
    $[64] = deliverArtifact;
    $[65] = email;
    $[66] = guideSource;
    $[67] = showArtifact;
    $[68] = slug;
    $[69] = track;
    $[70] = trackArtifactTaken;
    $[71] = visitorId;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  const handleSubmit = t17;
  let t18;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setOpenMatch(null);
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  let t19;
  if ($[74] !== handleStartTrial || $[75] !== openMatch) {
    t19 = <PreviewMatchModal onClose={t18} onStartTrial={handleStartTrial} rec={openMatch} />;
    $[74] = handleStartTrial;
    $[75] = openMatch;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  let t20;
  if ($[77] !== artifactPreview || $[78] !== deliverArtifact) {
    t20 = artifactPreview === null ? null : <div className="fixed inset-0 z-50 flex flex-col bg-black/70 p-2 sm:p-6"><div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-hidden rounded-fs-card"><div className="flex flex-wrap items-center justify-between gap-x-fs-sm gap-y-1 bg-fs-text px-3 py-2 sm:px-4"><span className="font-semibold text-fs-body-lg text-white">Your readout</span><div className="flex gap-fs-xs"><BrandButton className="px-4 py-2 text-[16px]" onClick={deliverArtifact} type="button">Download</BrandButton><BrandButton className="border-white/40 bg-white/10 px-4 py-2 text-[16px] text-white hover:bg-white/20" onClick={() => setArtifactPreview(null)} type="button" variant="outline">Close</BrandButton></div></div><iframe className="w-full flex-1 bg-white" sandbox="" srcDoc={artifactPreview} title="Your Extern Labs readout" /></div></div>;
    $[77] = artifactPreview;
    $[78] = deliverArtifact;
    $[79] = t20;
  } else {
    t20 = $[79];
  }
  let t21;
  if ($[80] !== accent) {
    t21 = <Confetti accent={accent} />;
    $[80] = accent;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  let t22;
  if ($[82] !== accent) {
    t22 = <span aria-hidden={true} className="absolute inset-y-0 left-0 w-1" style={{
      backgroundColor: accent
    }} />;
    $[82] = accent;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  let t23;
  if ($[84] !== accent) {
    t23 = {
      backgroundColor: accent
    };
    $[84] = accent;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Sparkles className="size-5" strokeWidth={2} />;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== t23) {
    t25 = <span aria-hidden={true} className="flex size-10 shrink-0 items-center justify-center rounded-fs-pill text-fs-on-primary" style={t23}>{t24}</span>;
    $[87] = t23;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== accent) {
    t26 = <span className="font-medium text-[12px] uppercase leading-[14px] tracking-[0.06em]" style={{
      color: accent
    }}>Your result</span>;
    $[89] = accent;
    $[90] = t26;
  } else {
    t26 = $[90];
  }
  let t27;
  if ($[91] !== accent) {
    t27 = {
      color: accent
    };
    $[91] = accent;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  const t28 = arch?.displayName;
  let t29;
  if ($[93] !== t27 || $[94] !== t28) {
    t29 = <span className="font-instrument text-[30px] leading-[1.1] tracking-[-0.5px]" style={t27}>{t28}</span>;
    $[93] = t27;
    $[94] = t28;
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  const t30 = arch?.tagline;
  let t31;
  if ($[96] !== t30) {
    t31 = <span className="mt-1 text-fs-body-md text-fs-text-subtle italic">{t30}</span>;
    $[96] = t30;
    $[97] = t31;
  } else {
    t31 = $[97];
  }
  let t32;
  if ($[98] !== t26 || $[99] !== t29 || $[100] !== t31) {
    t32 = <div className="flex flex-col">{t26}{t29}{t31}</div>;
    $[98] = t26;
    $[99] = t29;
    $[100] = t31;
    $[101] = t32;
  } else {
    t32 = $[101];
  }
  let t33;
  if ($[102] !== t25 || $[103] !== t32) {
    t33 = <div className="flex items-center gap-3">{t25}{t32}</div>;
    $[102] = t25;
    $[103] = t32;
    $[104] = t33;
  } else {
    t33 = $[104];
  }
  const t34 = arch?.body;
  let t35;
  if ($[105] !== t34) {
    t35 = <p className="mt-4 text-fs-body-lg text-fs-text-subtle leading-relaxed">{t34}</p>;
    $[105] = t34;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  let t36;
  if ($[107] !== t22 || $[108] !== t33 || $[109] !== t35) {
    t36 = <div className="relative overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface p-7 shadow-[0_1px_2px_rgba(10,14,26,0.04),0_8px_24px_rgba(10,14,26,0.05)]">{t22}{t33}{t35}</div>;
    $[107] = t22;
    $[108] = t33;
    $[109] = t35;
    $[110] = t36;
  } else {
    t36 = $[110];
  }
  let t37;
  if ($[111] !== accent || $[112] !== arch?.displayName || $[113] !== artifactSummary || $[114] !== consented || $[115] !== deliverArtifact || $[116] !== email || $[117] !== error || $[118] !== handleAccountFromArtifact || $[119] !== handleSubmit || $[120] !== showArtifact || $[121] !== submitting || $[122] !== trackArtifactTaken || $[123] !== unlocked) {
    t37 = unlocked ? <div className="flex flex-col gap-fs-sm rounded-fs-card border border-fs-border bg-fs-surface-subtle p-fs-md"><div className="flex flex-col gap-1"><h2 className="font-semibold text-fs-text text-fs-title-lg">Your readout is ready</h2><p className="text-fs-body-md text-fs-text-subtle">Saved to your downloads. Print it to PDF to keep it anywhere.</p></div><div className="flex flex-wrap gap-fs-xs"><BrandButton onClick={() => {
          showArtifact();
          trackArtifactTaken(true);
        }} type="button" variant="outline">Open it</BrandButton><BrandButton onClick={() => {
          deliverArtifact();
          trackArtifactTaken(false);
        }} type="button" variant="outline">Download it again</BrandButton></div><div className="flex flex-col gap-fs-xs border-fs-border border-t pt-fs-sm"><p className="text-fs-body-md text-fs-text-subtle">That was the short version. The full one is a real project with a real company, and we already have your email, so there is one step left.</p><BrandButton className="self-start" icon={ArrowRight} onClick={handleAccountFromArtifact} type="button">Create my free account</BrandButton></div></div> : <form className="flex flex-col gap-fs-sm rounded-fs-card border border-fs-border border-dashed bg-fs-surface-subtle p-fs-md" onSubmit={handleSubmit}><div className="flex items-start gap-fs-md"><div aria-hidden={true} className="w-[4.5rem] shrink-0 overflow-hidden rounded-lg border border-fs-border shadow-sm"><div className="flex flex-col gap-1.5 p-2.5" style={{
            backgroundColor: "#0b0e1a"
          }}><span className="block h-1 w-5 rounded-full" style={{
              backgroundColor: accent
            }} /><span className="block font-bold text-[8px] uppercase leading-[1.15] tracking-wide" style={{
              color: accent
            }}>{arch?.displayName}</span><span className="block h-0.5 w-full rounded bg-white/25" /><span className="block h-0.5 w-3/4 rounded bg-white/25" /><span className="block h-0.5 w-5/6 rounded bg-white/25" /></div><div className="flex flex-col gap-1 bg-white p-1.5"><span className="block h-0.5 w-2/3 rounded bg-fs-border" /><span className="block h-0.5 w-full rounded bg-fs-border" /></div></div><div className="flex flex-col gap-1"><h2 className="font-semibold text-fs-text text-fs-title-lg">Keep your readout</h2><p className="text-fs-body-md text-fs-text-subtle">{artifactSummary} Add your email and it downloads right away.</p></div></div><div className="flex flex-col gap-fs-xs sm:flex-row"><input aria-label="Email" autoComplete="email" className="flex-1 rounded-fs-button border border-fs-border bg-fs-surface px-4 py-3 text-fs-body-lg text-fs-text outline-none focus:border-fs-primary" id="labs-preview-email" inputMode="email" onChange={e => setEmail(e.target.value)} placeholder="you@university.edu" type="email" value={email} /><BrandButton className="shrink-0" disabled={submitting} type="submit" variant="outline">{submitting ? "One moment" : "Get my readout"}</BrandButton></div><label className="flex items-start gap-fs-xs text-fs-body-md text-fs-text-subtle" htmlFor="labs-preview-consent"><input checked={consented} className="mt-1 size-4 shrink-0 cursor-pointer" id="labs-preview-consent" onChange={e_0 => setConsented(e_0.target.checked)} type="checkbox" /><span>Also email me new externships in this track. Unsubscribe any time.</span></label>{error ? <p className="text-fs-body-md text-fs-danger" role="alert">{error}</p> : null}</form>;
    $[111] = accent;
    $[112] = arch?.displayName;
    $[113] = artifactSummary;
    $[114] = consented;
    $[115] = deliverArtifact;
    $[116] = email;
    $[117] = error;
    $[118] = handleAccountFromArtifact;
    $[119] = handleSubmit;
    $[120] = showArtifact;
    $[121] = submitting;
    $[122] = trackArtifactTaken;
    $[123] = unlocked;
    $[124] = t37;
  } else {
    t37 = $[124];
  }
  let t38;
  if ($[125] !== accent || $[126] !== displayName || $[127] !== handleMatchOpen || $[128] !== matches || $[129] !== slug) {
    t38 = matches.length > 0 ? <div className="flex flex-col gap-fs-sm"><div className="flex flex-col gap-1"><h2 className="font-instrument text-[24px] text-fs-text leading-[1.15]">{isFieldLessPack(slug) ? "Where that takes you next" : `Where that takes you in ${displayName}`}</h2><p className="text-fs-body-md text-fs-text-subtle">Real companies, a project you can show people, and a certificate at the end. Open one to see what the work actually is. {TRIAL_TERMS}</p></div><div className={cn("grid gap-fs-md", matches.length > 1 ? "sm:grid-cols-2" : "sm:grid-cols-1")}>{matches.slice(0, 2).map(rec_1 => <MatchCard accent={accent} key={rec_1.title} onOpen={handleMatchOpen} rec={rec_1} />)}</div></div> : null;
    $[125] = accent;
    $[126] = displayName;
    $[127] = handleMatchOpen;
    $[128] = matches;
    $[129] = slug;
    $[130] = t38;
  } else {
    t38 = $[130];
  }
  let t39;
  if ($[131] !== t21 || $[132] !== t36 || $[133] !== t37 || $[134] !== t38) {
    t39 = <div className="relative flex flex-col gap-fs-lg py-fs-md">{t21}{t36}{t37}{t38}</div>;
    $[131] = t21;
    $[132] = t36;
    $[133] = t37;
    $[134] = t38;
    $[135] = t39;
  } else {
    t39 = $[135];
  }
  let t40;
  if ($[136] !== t19 || $[137] !== t20 || $[138] !== t39) {
    t40 = <StepShell contentAlign="top" showProgress={false}>{t19}{t20}{t39}</StepShell>;
    $[136] = t19;
    $[137] = t20;
    $[138] = t39;
    $[139] = t40;
  } else {
    t40 = $[139];
  }
  return t40;
}
function _temp6(s_3) {
  return s_3.m3Diagnosis;
}
function _temp5(s_2) {
  return s_2.m3Answers;
}
function _temp4(s_1) {
  return s_1.m1Answers;
}
function _temp3(s_0) {
  return s_0.m2Answers;
}
function _temp2(s) {
  return s.archetype;
}